import { CommonModule } from '@angular/common';
import type { HttpErrorResponse } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, computed, inject, viewChildren } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import {
  BxAccordionComponent,
  BxBadgeComponent,
  BxBadgeGroupColor,
  BxBadgeGroupComponent,
  BxButtonComponent,
  BxCardComponent,
  BxCardHeaderComponent,
  BxToggleComponent,
  BxTooltipDirective,
  DialogService,
  injectBxIcons,
  alertCircleIcon,
  alertOctagonIcon,
  bell04Icon,
  bellOff03Icon,
  checkIcon,
  dotIcon,
  helpCircleIcon,
  hourglass01Icon,
  minusIcon,
} from '@beyonnex/bx-angular-ui';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { SimpleConfirmModalComponent } from '../../../components/modals/simple-confirm-modal/simple-confirm-modal.component';
import { StatusBadgeComponent } from '../../../components/status-badge/status-badge.component';
import { traceparentOf } from '../../../interceptors/traceparent-interceptor.models';
import { formatDate, formatDateTime } from '../../../models/core.models';
import { findInternalActions } from '../../../models/device-status-action.models';
import { CARDINALITY, DeviceSeverityKey, DeviceStatusDimension, DeviceStatusOverall, isCardinality } from '../../../models/device.models';
import { CommonTranslateService } from '../../../services/common-translate/common-translate.service';
import { DeviceMutingService } from '../../../services/device-muting/device-muting.service';
import { DittoService } from '../../../services/ditto/ditto.service';
import { NotificationsService } from '../../../services/notifications/notifications.service';
import { DeviceDetailsStore } from '../device-details.store';

@Component({
  selector: 'bxi-device-status',
  imports: [
    CommonModule,
    TranslatePipe,
    StatusBadgeComponent,
    BxCardComponent,
    BxCardHeaderComponent,
    BxAccordionComponent,
    BxToggleComponent,
    BxBadgeGroupComponent,
    BxBadgeComponent,
    BxTooltipDirective,
    BxButtonComponent,
  ],
  templateUrl: './device-status.component.html',
  host: { class: 'block' },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DeviceStatusComponent {
  private readonly notifications = inject(NotificationsService);
  private readonly dialog = inject(DialogService);
  private readonly ditto = inject(DittoService);
  private readonly deviceMuting = inject(DeviceMutingService);
  private readonly translate = inject(TranslateService);
  private readonly commonTranslate = inject(CommonTranslateService);
  private readonly store = inject(DeviceDetailsStore);
  protected readonly device = toSignal(this.store.device$);
  protected readonly canMute = toSignal(this.store.canMute$, { initialValue: false });

  protected readonly dimensions = computed(() => {
    const dimensions = this.device()?.features?.deviceStatus?.properties?.status?.dimensions;
    const order = ['installation', 'connectivity', 'internal', 'analysis'];

    return Object.entries<DeviceStatusDimension>(dimensions ?? {})
      .map(([key, dimension]) => {
        if (!dimension) {
          return null;
        }

        return {
          ...dimension,
          badge: this.createStatusBadgeData('severityInfo', dimension),
          name: key,
          activeSince: dimension.severityInfo?.activeSince,
          isActive: this.isAccordionActive(dimension),
          hasFirstOccurrence: this.hasFirstOccurrence(dimension),
        };
      })
      .filter((dimension) => dimension !== null)
      .sort((a, b) => {
        return order.indexOf(a.name) - order.indexOf(b.name);
      });
  });

  protected readonly overallStatusBadge = computed(() =>
    this.createStatusBadgeData('technicalSeverity', this.device()?.features?.deviceStatus?.properties?.status),
  );
  protected readonly overallActiveSince = computed(
    () => this.device()?.features?.deviceStatus?.properties?.status?.technicalSeverity?.activeSince,
  );
  protected readonly overallInternalActions = computed(() =>
    findInternalActions(this.device()?.features?.deviceStatus?.properties?.status?.messages).map(
      this.commonTranslate.translateInternalAction,
    ),
  );

  protected readonly muting = computed(() => this.device()?.features?.monitoring?.properties?.configuration?.muting);
  protected readonly mutingPeriod = computed(() => {
    const muting = this.muting();
    if (!muting) {
      return '';
    }
    const fromRaw = muting?.period?.from;
    const toRaw = muting?.period?.to;
    let period = '';
    if (fromRaw) {
      period += formatDate(fromRaw);
    }

    if (toRaw) {
      period += ` - ${formatDate(toRaw)}`;
    }

    return this.translate.instant('deviceDetails.deviceStatus.muting.period', { period });
  });

  private readonly accordions = viewChildren(BxAccordionComponent);

  constructor() {
    injectBxIcons([
      checkIcon,
      alertCircleIcon,
      alertOctagonIcon,
      hourglass01Icon,
      minusIcon,
      helpCircleIcon,
      bellOff03Icon,
      bell04Icon,
      dotIcon,
    ]);
  }

  protected isCardinality = isCardinality;

  protected formatDate = formatDateTime;

  protected onExpandDimensions(event: Event): void {
    const toggle = event.target as HTMLInputElement;
    this.accordions().forEach((accordion) => accordion.setActive(toggle.checked));
  }

  protected setSubDimensionColor(cardinality?: number): boolean {
    if ((!cardinality && cardinality !== 0) || cardinality > 30) {
      return false;
    }

    return true;
  }

  protected isAccordionActive(dimension?: DeviceStatusDimension): boolean {
    if (!dimension) {
      return false;
    }

    return Object.values(dimension.subStates ?? {}).some(
      (item) => item.severityInfo && item.severityInfo.level?.cardinality && item.severityInfo.level?.cardinality >= CARDINALITY.WARNING,
    );
  }

  // TODO: Change implementation when BE return correct model
  protected hasFirstOccurrence(dimension?: DeviceStatusDimension): boolean {
    if (!dimension || Object.keys(dimension.subStates ?? {}).length === 0) {
      return false;
    }

    return true;
  }

  protected createStatusBadgeData(
    severityKey: DeviceSeverityKey,
    status?: DeviceStatusDimension & DeviceStatusOverall,
  ): {
    color: BxBadgeGroupColor;
    message: string | null;
    cardinality?: number;
  } {
    const cardinality = status?.[severityKey]?.level?.cardinality;

    return {
      color: colorOf(cardinality),
      cardinality,
      message: status?.messages ? status.messages.join(', ') : null,
    };
  }

  protected openMuteDialog(): void {
    const device = this.device();
    if (device) {
      this.deviceMuting.muteDevice(device).subscribe();
    }
  }

  protected openUnmuteDialog(): void {
    this.dialog
      .open(SimpleConfirmModalComponent, {
        data: { title: `${this.translate.instant('deviceDetails.deviceStatus.muting.unmuteButton')}?` },
        width: '400px',
      })
      .afterClosed$.subscribe((result) => {
        const thingId = this.device()?.thingId;
        if (thingId && result) {
          this.ditto.triggerAction(thingId, 'features/monitoring/inbox/messages/unmute').subscribe({
            next: () =>
              this.notifications.addSuccessNotification(
                this.translate.instant('deviceDetails.deviceStatus.muting.successNotificationUnmute'),
                '',
              ),
            error: (err: HttpErrorResponse) => this.notifications.addGenericErrorNotification(err.message, traceparentOf(err)),
          });
        }
      });
  }
}

// Not the library's BX_CARDINALITY_COLOR: bx-badge-group has no gray-blue or indigo, so EMPTY and PENDING take the nearest group colour.
function colorOf(cardinality?: number): BxBadgeGroupColor {
  switch (cardinality) {
    case CARDINALITY.OK:
      return 'success';
    case CARDINALITY.EMPTY:
    case CARDINALITY.UNKNOWN:
      return 'gray';
    case CARDINALITY.WARNING:
      return 'warning';
    case CARDINALITY.ERROR:
      return 'error';
    default:
      return 'brand';
  }
}
